/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { BADGE_TYPE_STRONG, Badge } from '@squiz/sds';
import { getCapabilityPath } from 'apps/console-core/src/Utilities/GetCapabilityPath/GetCapabilityPath';
import React, { KeyboardEvent, MouseEvent, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { truncateDisplayString } from '../../../../../src/Utilities/TruncateDisplayString/TruncateDisplayString';
import { MaterialIcon } from '../../../../Components/MaterialIcon/MaterialIcon';
import { COMPONENT_SERVICE_STRINGS } from '../../../../Constants/Strings.constants';
import { getComponentPath } from '../../../../Utilities/GetComponentPath/GetComponentPath';
import { ALL_COMPONENTS_CONSTANTS, ALL_COMPONENTS_DATA_IDS } from '../../AllComponents.constants';
import { ComponentTypeBadge } from '../ComponentTypeBadge/ComponentTypeBadge';
import { AllComponentsItemProps } from './AllComponentsItem.props';

/**
 * All components list item.
 * @param {AllComponentsItemProps} props Props passed to `<AllComponentsItem />`.
 * @export
 * @returns {JSX.Element} The `<AllComponentsItem />` component.
 */
export const AllComponentsItem = (props: AllComponentsItemProps): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { component } = props;

  const labels = {
    instances: t(COMPONENT_SERVICE_STRINGS.instances),
    sets: t(COMPONENT_SERVICE_STRINGS.sets),
  };

  const icon = component.latestVersion.icon;
  const componentType = component.latestVersion.type;

  // Component details path handling
  const componentName = `/${component.name.split('/')[1]}`;
  const componentNamespace = component.name.split('/')[0];

  const currentScreenPath = getCapabilityPath(`/all-components`);
  const componentDetailsPath = `${currentScreenPath}${getComponentPath(componentName, componentNamespace)}`;

  const navigateToComponentDetails = useCallback((): void => {
    navigate({ pathname: componentDetailsPath });
  }, [componentDetailsPath, navigate]);

  const goToComponentDetailsMouseHandler = (event: MouseEvent<HTMLButtonElement>): void => {
    navigateToComponentDetails();
  };

  const goToComponentDetailsKeyboardHandler = (event: KeyboardEvent<HTMLButtonElement>): void => {
    // Keys which should navigate user to the segment.
    const navigateKeys = ['Enter', 'Space'];
    if (navigateKeys.includes(event.code)) {
      navigateToComponentDetails();
    }
  };

  const truncatedDescription = useMemo(
    () => truncateDisplayString(component.latestVersion.description, ALL_COMPONENTS_CONSTANTS.maxDescriptionLength),
    [component.latestVersion.description],
  );

  const truncatedDisplayName = useMemo(
    () => truncateDisplayString(component.latestVersion.displayName, ALL_COMPONENTS_CONSTANTS.maxDisplayNameLength),
    [component.latestVersion.displayName],
  );

  return (
    <div className="all-components__item">
      <button
        className="all-components__link"
        onClick={goToComponentDetailsMouseHandler}
        onKeyDown={goToComponentDetailsKeyboardHandler}
      >
        <div className="all-components__card">
          <div className="all-components__body">
            {/* Component Title & Path */}
            <div className="all-components__header">
              <div className="all-components__icon" data-id={ALL_COMPONENTS_DATA_IDS.componentIcon}>
                {icon ? <MaterialIcon color={icon.color.value} name={icon.id} /> : <MaterialIcon />}
              </div>
              <div className="all-components__header-text">
                <span className="all-components__title" data-id={ALL_COMPONENTS_DATA_IDS.componentTitle}>
                  {truncatedDisplayName}
                </span>
                <span className="all-components__subtitle" data-id={ALL_COMPONENTS_DATA_IDS.componentWebpath}>
                  {component.name}
                </span>
              </div>
            </div>

            {/* Component Contents */}
            <div className="all-components__content">
              <span className="all-components__description" data-id={ALL_COMPONENTS_DATA_IDS.componentDescription}>
                {truncatedDescription}
              </span>
            </div>

            {/* Component Stats (Instance & Set Count) */}
            <div className="all-components__footer">
              <div className="all-components__stats">
                <ComponentTypeBadge componentType={componentType} />
              </div>
              <span className="all-components__separator" />
              <div className="all-components__stats">
                <Badge left={String(component.includedInSets)} type={BADGE_TYPE_STRONG} /> {labels.sets}
              </div>
            </div>
          </div>
        </div>
      </button>
    </div>
  );
};
